import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICompanyCard, IMyProfile, IUserCard } from '@share/common-types';
import { AppThunk } from '@share/utils';
import { AddCompanyCreditCard, DeleteCompanyCreditCard, GetCompanyCreditCards } from '@share/services';
import { NULL_VALUE } from '@constants';

export interface ICompanyProfileState {
  loading: boolean;
  loadingCards: boolean;
  error: string;
  result: {
    profile: IMyProfile;
    authorizedUsers: IMyProfile[];
    companyCards: ICompanyCard[];
  }
}

const initialState: ICompanyProfileState = {
  loading: false,
  loadingCards: false,
  error: '',
  result: {
    profile: NULL_VALUE,
    authorizedUsers: [],
    companyCards: []
  }
};

const companyProfileSlice = createSlice({
  name: 'companyProfile',
  initialState,
  reducers: {
    setLoading: (state: ICompanyProfileState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setLoadingCards: (state: ICompanyProfileState, { payload }: PayloadAction<boolean>) => {
      state.loadingCards = payload;
    },
    setError: (state: ICompanyProfileState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setMyProfile: (state: ICompanyProfileState, { payload }: PayloadAction<IMyProfile>) => {
      state.result.profile = payload;
    },
    setAuthorizedUsers: (state: ICompanyProfileState, { payload }: PayloadAction<IMyProfile[]>) => {
      state.result.authorizedUsers = payload;
    },
    setCompanyCards: (state: ICompanyProfileState, { payload }: PayloadAction<ICompanyCard[]>) => {
      state.result.companyCards = payload;
    },
  },
});

export const companyProfileActions = companyProfileSlice.actions;

export const companyProfileReducer = companyProfileSlice.reducer;

// export const getMyProfile = (): AppThunk => {
//   return async (dispatch, getState) => {
//     const { loginStore } = getState();
//     const { user, account } = loginStore;

//     const isPrivateTokenAccountType = account?.isPrivateTokenAccountType;
//     if (!isPrivateTokenAccountType) {
//       return;
//     }

//     dispatch(companyProfileActions.setLoading(true));
//     dispatch(companyProfileActions.setMyProfile(NULL_VALUE));

//     try {
//       const response = await MyProfileAPI.get(user.keyid);
//       const { data } = response;

//       if (data.isSuccess) {
//         dispatch(companyProfileActions.setMyProfile(data.item));
//         dispatch(companyProfileActions.setAuthorizedUsers(data.authorizedUsers));
//       } else {
//         dispatch(companyProfileActions.setError(`[${data.status}] ${data.message}`));
//       }
//       dispatch(companyProfileActions.setLoading(false));

//       dispatch(getCompanyCards());
//     } catch (error: any) {
//       console.error(error);
//       dispatch(companyProfileActions.setError(error.toString()));
//       dispatch(companyProfileActions.setLoading(false));
//     }
//   };
// };

// export const updateMyProfile = (profile: IMyProfile): AppThunk => {
//   return async (dispatch, getState) => {
//     dispatch(companyProfileActions.setLoading(true));

//     try {
//       const { loginStore } = getState();
//       const { user, userWallet } = loginStore;

//       const response = await MyProfileAPI.update(user.keyid, profile);
//       const { data } = response;

//       if (data.isSuccess) {
//         dispatch(setLoginUser(data.user));
//         dispatch(setLoginOriginalUser(data.user));
        
//         if (userWallet) {
//           dispatch(getUserWallet(data.user));
//         }
//         dispatch(getMyProfile());
//         Toaster.success(data.message);
//       } else {
//         dispatch(companyProfileActions.setError(`[${response.status}] ${data.message}`));
//         Toaster.error(`An error occurred. [${response.status}] ${data.message}. If the problem persists, please contact the system administrator.`);
//       }
//       dispatch(companyProfileActions.setLoading(false));
//     } catch (error: any) {
//       console.error(error);
//       dispatch(companyProfileActions.setError(error.toString()));
//       dispatch(companyProfileActions.setLoading(false));
//       Toaster.error(`An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`);
//     }
//   };
// };


export const getCompanyCards = (): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(companyProfileActions.setCompanyCards(NULL_VALUE));
      dispatch(companyProfileActions.setLoadingCards(true));

      const response = await GetCompanyCreditCards();
      dispatch(companyProfileActions.setCompanyCards(response?.data?.map((c: any) => ({
        id: c?.id,
        addressLine: c?.billing?.address?.line1,
        cvv: null,
        cardNumber: `**** **** **** ${c?.card?.last4}`,
        cardType: c?.card?.brand,
        city: c?.billing?.address?.city,
        country: c?.billing?.address?.country,
        state: c?.billing?.address?.state,
        expireDate: `${c?.card?.expMonth < 10 ? '0' : ''}${c?.card?.expMonth}${c?.card?.expYear}`,
        holderName: c?.billing?.name,
        phone: c?.billing?.name,
        zipCode: c?.billing?.address?.postalCode,
      } as any))));
      dispatch(companyProfileActions.setLoadingCards(false));
    } catch(error) {
      console.error(error);
      dispatch(companyProfileActions.setLoadingCards(false));
    }
  }
}

export const addCompanyCard = (companyCard: ICompanyCard, sucessCallBack: () => void): AppThunk => {
  return async (dispatch, getState) => {
    try {
      const { loginStore } = getState();
      const { account } = loginStore;
      dispatch(companyProfileActions.setLoadingCards(true));

      await AddCompanyCreditCard(companyCard, account);
      
      dispatch(getCompanyCards());
      dispatch(companyProfileActions.setLoadingCards(false));
      sucessCallBack();
    } catch(error) {
      console.error(error);
      dispatch(companyProfileActions.setLoadingCards(false));
    }
  }
}

export const deleteCompanyCard = (companyCard: ICompanyCard, sucessCallBack: () => void): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(companyProfileActions.setLoadingCards(true));
      
      await DeleteCompanyCreditCard(companyCard?.id);
      
      dispatch(getCompanyCards());
      dispatch(companyProfileActions.setLoadingCards(false));

      sucessCallBack();
    } catch(error) {
      console.error(error);
      dispatch(companyProfileActions.setLoadingCards(false));
    }
  }
}
